import { NavigateBefore, NavigateNext, PriorityHigh } from '@mui/icons-material';
import AvTimerIcon from '@mui/icons-material/AvTimer';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import QuestionMarkOutlinedIcon from '@mui/icons-material/QuestionMarkOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import {
  Backdrop,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { therapyPlanningCaseMixTemplates, therapyPlanningTemplate } from 'src/config';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import { ignoreGroup, shouldShowAlertIcon } from 'src/layouts/components/utils-method';
import { CalenderIcon } from 'src/pages/therapist-dashboard/dashboard-icon';
import { paths } from 'src/paths';
import { Permissions, useScopeChecker } from 'src/rbac';
import {
  getEnterprisePayerPlanType,
  getPatientIdAction,
  resetSelectPatient,
  selectPatient,
  switchTherapistDisciplineAction,
} from 'src/redux/actions/patientAction';
import {
  getAuthorizationDetails,
  getCmgResolution,
  getDischargeDatesOfTherapyPlanning2_0,
  getMdsAssessment,
  getPatientDateOfService,
  getPdpmFeatureToggle,
  getPayerDayCountAction,
  getPlanOfCare,
  getTherapyPlanningPatient_2_0,
  resetGetAllTherapyTemplates,
  resetGetTherapyPlanningPatient_2_0,
} from 'src/redux/actions/therapyPlanning';
import patientListIcon from '../../icons/untitled-ui/duocolor/patientListIcon.svg';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import { DotIcon, ManageTemplIcon, PlanOfCareIcon } from './commonComponent/therapy-planning-icons';
import TherapPlanningLoadingPage from './commonComponent/therapy-planning-loading-page';
import TherapyPlaningNavigation from './therapy-planing-navigation';
import { TherapyPlanningPatientList } from './therapy-planing-patient-list-2.0';
import RecommendedRangeHeader from './RecommendedRangeHeader';
import ManageTherapyPlanningTemplate from './therapy-planning-manage-template';
import { therapyPlanningRowTestId, therapyPlanningTestId } from './test-ids';
import { neutral } from 'src/theme/colors';

const { therapyPlanning, patients, patientDetails, episodeOfCare, details } = paths;

const navButtonSx = {
  width: '32px',
  height: '32px',
  padding: '10px',
  borderRadius: '8px',
  border: '1px solid #F1F1F1',
  background: 'rgba(99, 102, 241, 0.10)',
  '&:hover': {
    background: 'rgba(99, 102, 241, 0.10)',
  },
};

const iconBoxSx = {
  width: '35px',
  height: '35px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#e8edff',
  borderRadius: '12px',
};

const LoadingSkeletons = ({ height }: { height: number }) => (
  <Stack
    sx={{ marginTop: '2rem' }}
    gap="1.5rem"
  >
    <Skeleton
      height={height}
      variant="rounded"
    />
    <Skeleton
      variant="rounded"
      height={height}
      animation="wave"
    />
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rounded"
        height={height}
        animation={false}
      />
    ))}
  </Stack>
);

const tabs = [
  { label: 'Therapy Planning', value: 'info' },
  { label: 'MDS Assessments', value: 'details' },
  {
    label: 'Authorizations',
    value: 'authorization',
  },
];

const AlertIcons = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M6.99969 5.2472V7.58054M6.99969 9.91387H7.00553M6.19196 2.26738L1.39411 10.5546C1.12799 11.0142 0.994934 11.2441 1.0146 11.4327C1.03175 11.5972 1.11795 11.7467 1.25174 11.844C1.40514 11.9555 1.67071 11.9555 2.20185 11.9555H11.7975C12.3287 11.9555 12.5943 11.9555 12.7476 11.844C12.8814 11.7467 12.9676 11.5972 12.9848 11.4327C13.0045 11.2441 12.8714 11.0142 12.6053 10.5546L7.80742 2.26737C7.54226 1.80936 7.40968 1.58036 7.2367 1.50344C7.08582 1.43635 6.91357 1.43635 6.76269 1.50344C6.58971 1.58036 6.45713 1.80936 6.19196 2.26738Z"
        stroke="#B54708"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const TherapyPlanningDetails_2_0 = () => {
  const navigate = useNavigate();
  const has = useScopeChecker();

  const [firstRender, setFirstRender] = useState(true);
  const [open, setOpen] = useState(false);
  const [therapyTemplatesEnabled] = useFeatureToggle(therapyPlanningTemplate, 'ENTERPRISE');
  const therapyCaseMixTemplatesEnabled = useSelector(
    (state: any) => state?.getPdpmFeatureToggleReducer?.response?.data
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [multipleMdsAssessment, setMultipleMdsAssessment] = useState({
    open: [],
    closed: [],
  });

  const [openManageTempDialog, setManageTempDialog] = useState(false);
  const dateOfServiceResponse = useSelector(
    (state: any) => state?.getDateOfServiceReducer?.response?.data
  );
  const isRequiredPlanning = useSelector(
    (state: any) => state.getTherapyPlanningSummaryReducer_2_0?.isRequiredPlanning
  );
  const getPayerPlanTypeResponse = useSelector(
    (state: any) => state.getPayerPlanTypeReducer?.enterprisePayerPlanType?.data
  );

  const patientResponse_2_0 = useSelector(
    (state: any) => state.getTherapyPlanningPatientReducer_2_0
  );
  const authDetails = useSelector(
    (state: any) => state.getAuthorizationDetailsReducer?.response?.data
  );
  const updateTherapyPlanningResponse = useSelector(
    (state: any) => state?.updateTherapyPlanningReducer
  );

  const getTherapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );
  const getMultipleMdsAssessment = useSelector((state: any) => state.getMultipleMDSAssessment);
  const selectedPatient = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);
  // THIN-8155: the header names the payer the visible window is counted under — the latest row in
  // view that has one — not the payer active today.
  const windowPayer = useSelector((state: any) =>
    [...(state?.getPayerDayCountReducer?.response?.data ?? [])]
      .reverse()
      .find((item: any) => item?.payerUuid)
  );
  const headerPayerPlanName = windowPayer?.payerPlanName ?? selectedPatient?.payerPlanName;
  const cmgResolution = useSelector((state: any) => state?.getCmgResolutionReducer);
  const planOfCareDetails = useSelector(
    (state: any) => state?.getPlanOfCareReducer?.response?.data
  );
  const planOfCareResponse = useSelector((state: any) => state?.getPlanOfCareReducer);
  const dispatch = useDispatch();
  const { state, pathname: routerPathname } = useLocation();
  const [searchTerm, setSearchTerm] = useState<string>(state?.searchData ? state?.searchData : '');
  const [selectedDisciplines, setSelectedDisciplines] = useState<string[]>([]);
  const disciplineChangedRef = useRef(false);
  const isTabletScreen = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));

  const handleDisciplineChange = (discipline: string) => {
    disciplineChangedRef.current = true;
    setSelectedDisciplines((prev) =>
      prev.includes(discipline) ? prev.filter((d) => d !== discipline) : [...prev, discipline]
    );
    setCurrentIndex(0);
  };

  const onClose = () => {
    setOpen(false);
  };
  const patientId = state?.patientId;

  // THIN-7684: fetch the resolved CMG + recommended-weekly-range for the selected
  // resident. Re-runs when the resident cycles or the tab changes so the header stays current.
  useEffect(() => {
    if (selectedPatient?.uuid) {
      dispatch(getCmgResolution({ patientId: selectedPatient?.uuid }));
    }
  }, [selectedPatient?.uuid, routerPathname]);

  useEffect(() => {
    if (selectedPatient?.uuid) {
      dispatch(getPdpmFeatureToggle({ patientId: selectedPatient?.uuid }));
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (getMultipleMdsAssessment?.response?.data?.length) {
      const getOpenEoc = getMultipleMdsAssessment?.response?.data?.filter(
        (mds: any) => mds?.status === 'OPENED'
      );
      const getClosed = getMultipleMdsAssessment?.response?.data?.filter(
        (mds: any) => mds?.status === 'CLOSED'
      );
      setMultipleMdsAssessment((prev) => ({
        ...prev,
        open: getOpenEoc,
        closed: getClosed,
      }));
    }
  }, [getMultipleMdsAssessment]);

  useEffect(() => {
    if (selectedPatient) {
      dispatch(getPlanOfCare({ patientId: selectedPatient?.uuid }));
      dispatch(
        getDischargeDatesOfTherapyPlanning2_0({
          patientId: selectedPatient?.uuid,
        })
      );
    }
  }, [selectedPatient]);
  useEffect(() => {
    if (selectedPatient?.uuid) {
      dispatch(
        getAuthorizationDetails({
          patientId: selectedPatient?.uuid,
        })
      );
      dispatch(
        getPatientDateOfService({
          patientId: selectedPatient?.uuid,
        })
      );
      dispatch(
        getMdsAssessment({
          patientId: selectedPatient?.uuid,
        })
      );
    }
  }, [selectedPatient?.uuid]);

  // THIN-8297: the header reads this reducer too, but only the Therapy Planning tab
  // populated it — fetch it here so patient switches update the header on any tab.
  useEffect(() => {
    if (!selectedPatient?.uuid) return;
    const today = moment();
    dispatch(
      getPayerDayCountAction({
        patientId: selectedPatient?.uuid,
        params: {
          startDate: today.format('YYYY-MM-DD'),
          endDate: today.clone().add(8, 'days').format('YYYY-MM-DD'),
        },
      })
    );
  }, [selectedPatient?.uuid]);

  const mdsAssessmentResponse = useSelector((state: any) => state?.getMDSAssessmentReducer);
  const mdsAssessmentDetails = useSelector(
    (state: any) => state?.getMDSAssessmentReducer?.response?.data
  );

  const renderFrequencyDuration = (
    disipline: 'OT' | 'SLP' | 'PT',
    source: 'frequency' | 'duration'
  ) => {
    return (
      <Stack
        // gap="1rem"
        alignItems="center"
        flexDirection="row"
        position={'relative'}
        title={source}
      >
        <Chip
          variant="outlined"
          label={disipline}
        />
        <Box
          ml={8}
          position={'absolute'}
        >
          {source === 'frequency' ? <CalendarMonthIcon /> : <AvTimerIcon />}
        </Box>
        <Typography
          position={'absolute'}
          ml={15}
          variant="body2"
        >
          {source === 'frequency'
            ? planOfCareDetails?.disciplineMap[disipline] || '-'
            : planOfCareDetails?.durationMap[disipline] || '-'}
        </Typography>
      </Stack>
    );
  };

  const renderCertDate = (disipline: 'pt' | 'ot' | 'slp') => {
    const startKey = `${disipline}CertStartDate`;
    const endKey = `${disipline}CertEndDate`;

    return (
      <Stack
        alignItems="center"
        flexDirection="row"
        position={'relative'}
      >
        <Chip
          variant="outlined"
          label={disipline?.toUpperCase()}
        />
        <Typography
          variant="body2"
          position={'absolute'}
          ml={15}
        >
          {planOfCareDetails?.[startKey]
            ? format(
                new Date(
                  new Date(planOfCareDetails?.[startKey]).valueOf() +
                    new Date(planOfCareDetails?.[startKey]).getTimezoneOffset() * 60 * 1000
                ),
                'MMM dd, yyyy'
              )
            : '-'}
        </Typography>
        <Typography
          variant="body2"
          position={'relative'}
          ml={30}
        >
          {planOfCareDetails?.[endKey]
            ? format(
                new Date(
                  new Date(planOfCareDetails?.[endKey]).valueOf() +
                    new Date(planOfCareDetails?.[endKey]).getTimezoneOffset() * 60 * 1000
                ),
                'MMM dd, yyyy'
              )
            : '-'}
        </Typography>
      </Stack>
    );
  };

  useEffect(() => {
    dispatch(getEnterprisePayerPlanType());
    return () => {
      if (!location.pathname.startsWith(therapyPlanning.index)) {
        dispatch(resetSelectPatient());
      }
    };
  }, []);

  useEffect(() => {
    const debounceSearch = _.debounce(() => {
      dispatch(resetGetTherapyPlanningPatient_2_0());
      dispatch(
        getTherapyPlanningPatient_2_0({
          params: {
            search: searchTerm,
            page: 0,
            pageSize: 20,
            ...(selectedDisciplines.length > 0 && {
              activeDisciplines: selectedDisciplines.join(','),
            }),
          },
          isFirstTimeCall: firstRender,
        })
      );
    }, 1000);

    if (!state?.flag) debounceSearch();
    setFirstRender(false);

    return () => debounceSearch.cancel();
  }, [searchTerm, selectedDisciplines]);

  useEffect(() => {
    if (!disciplineChangedRef.current || patientResponse_2_0?.loading) return;
    disciplineChangedRef.current = false;

    if (patientResponse_2_0?.totalPatient?.length > 0) {
      setCurrentIndex(0);
      dispatch(selectPatient(patientResponse_2_0?.totalPatient[0]));
    } else {
      dispatch(resetSelectPatient());
    }
  }, [patientResponse_2_0?.totalPatient, patientResponse_2_0?.loading]);

  const getActualIndex = () => {
    const currentPatientIndex = patientResponse_2_0?.totalPatient?.findIndex(
      (patient: any) => patient?.uuid === selectedPatient?.uuid
    );
    return currentPatientIndex !== -1 ? currentPatientIndex : currentIndex;
  };

  const handleNext = () => {
    const newIndex = getActualIndex() + 1;
    setCurrentIndex(newIndex);
    dispatch(selectPatient(patientResponse_2_0?.totalPatient[newIndex]));

    // Check if we need to load more data
    if (
      newIndex + 5 >= patientResponse_2_0?.totalPatient?.length &&
      patientResponse_2_0?.response?.data?.totalElements !==
        patientResponse_2_0?.totalPatient?.length &&
      !patientResponse_2_0?.loading
    ) {
      dispatch(
        getTherapyPlanningPatient_2_0({
          params: {
            search: '',
            page: patientResponse_2_0?.pageNumber,
            pageSize: 20,
            ...(selectedDisciplines?.length > 0 && {
              activeDisciplines: selectedDisciplines.join(','),
            }),
          },
          isFirstTimeCall: false,
        })
      );
    }
  };

  const handlePrevious = () => {
    const newIndex = getActualIndex() - 1;
    setCurrentIndex(newIndex);
    dispatch(selectPatient(patientResponse_2_0?.totalPatient[newIndex]));
  };

  const payerPlanName = getPayerPlanTypeResponse?.find(
    (res: any) => res?.key === planOfCareDetails?.payerType
  )?.displayName;

  // THIN-7684: map the resolved CMG response into the recommended-range header.
  // Group minutes come back as "min-max" strings (e.g. "10-23").
  const parseGroupMinutes = (value?: string) => {
    if (!value) return { min: null, max: null };
    const [min, max] = value.split('-').map((n) => n?.trim());
    return {
      min: min ? Number(min) : null,
      max: max ? Number(max) : null,
    };
  };
  const cmgData = cmgResolution?.response?.data;
  const hasCmg = !!(cmgData?.ptOtGroup || cmgData?.slpGroup);
  const isPdpmShape = !!cmgData && ('ptOtGroup' in cmgData || 'slpGroup' in cmgData);
  const recommendedRangeData = {
    hasMds: hasCmg,
    isPdpm: isPdpmShape,
    source: cmgData?.source ?? null,
    ptOt: { cmg: cmgData?.ptOtGroup ?? null, ...parseGroupMinutes(cmgData?.ptOtGroupMinutes) },
    slp: { cmg: cmgData?.slpGroup ?? null, ...parseGroupMinutes(cmgData?.slpGroupMinutes) },
  };

  return (
    <>
      <Container
        maxWidth="xl"
        sx={{ padding: '0px !important' }}
      >
        <Stack
          spacing={3}
          mt={1}
        >
          <Stack spacing={3}>
            <Grid
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  data-testid={therapyPlanningTestId('heading')}
                >
                  Therapy Planning
                </Typography>
                <Box>
                  {patientResponse_2_0?.loading ? (
                    <Box sx={{ width: 100, ml: 3 }}>
                      <Typography
                        width={'100%'}
                        variant="h4"
                      >
                        <Skeleton animation="pulse" />
                      </Typography>
                    </Box>
                  ) : (
                    <Chip
                      label={`${
                        patientResponse_2_0?.response?.data?.totalElements
                          ? patientResponse_2_0?.response?.data?.totalElements
                          : '0'
                      } Patients`}
                      variant="outlined"
                      data-testid={therapyPlanningTestId('patient-count')}
                      sx={{ marginLeft: '10px', border: '1px solid  rgba(0, 0, 0, 0.26)' }}
                    />
                  )}
                </Box>
                <Box>
                  <TherapyPlanningPatientList
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    selectedPatient={selectedPatient}
                    setCurrentIndex={setCurrentIndex}
                    selectedDisciplines={selectedDisciplines}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 4 }}>
                  {['PT', 'OT', 'SLP']?.map((discipline) => (
                    <FormControlLabel
                      key={discipline}
                      control={
                        <Checkbox
                          checked={selectedDisciplines?.includes(discipline)}
                          onChange={() => handleDisciplineChange(discipline)}
                          size="small"
                          inputProps={
                            {
                              'data-testid': therapyPlanningRowTestId(
                                'discipline-filter',
                                discipline
                              ),
                            } as InputHTMLAttributes<HTMLInputElement>
                          }
                        />
                      }
                      label={discipline}
                    />
                  ))}
                </Box>
              </Box>

              {has(Permissions.THERAPY_TEMPLATE) && therapyTemplatesEnabled ? (
                <Button
                  variant="text"
                  startIcon={<ManageTemplIcon />}
                  sx={{
                    color: '#6366F1',
                    margin: '0px',
                    padding: '0px',
                    paddingBottom: '2px',
                    cursor: 'pointer',
                    borderRadius: 'unset',
                    backgroundColor: 'transparent',
                    width: 'fit-content',
                  }}
                  onClick={() => {
                    dispatch(resetGetAllTherapyTemplates());
                    setManageTempDialog(true);
                  }}
                >
                  Manage Templates
                </Button>
              ) : null}
            </Grid>
            <Divider sx={{ border: '1px solid #E5E7EB' }} />

            {!selectedPatient?.uuid && patientResponse_2_0?.loading ? (
              <TherapPlanningLoadingPage />
            ) : (
              <Stack
                flexDirection="row"
                gap="1rem"
                sx={{ margin: '0px !important', overflow: 'auto' }}
              >
                {selectedPatient ? (
                  <Stack
                    sx={{
                      flex: '8',
                      paddingTop: '1.5rem',
                      minWidth: '675px',
                      paddingRight: '1rem',
                    }}
                  >
                    <Stack
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems={'center'}
                      mb={1}
                    >
                      <Stack
                        gap="1rem"
                        alignItems="center"
                        flexDirection="row"
                      >
                        <Stack
                          gap="1rem"
                          alignItems="center"
                          flexDirection="row"
                        >
                          <Tooltip
                            title="Previous Patient"
                            placement="top"
                          >
                            <IconButton
                              sx={navButtonSx}
                              disabled={!patientResponse_2_0?.totalPatient[currentIndex - 1]?.uuid}
                              onClick={handlePrevious}
                              data-testid={therapyPlanningTestId('previous-patient')}
                            >
                              <NavigateBefore sx={{ color: '#6366F1', fontSize: '14px' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title="Next Patient"
                            placement="top"
                          >
                            <IconButton
                              onClick={handleNext}
                              data-testid={therapyPlanningTestId('next-patient')}
                              disabled={!patientResponse_2_0?.totalPatient[currentIndex + 1]?.uuid}
                              sx={navButtonSx}
                            >
                              <NavigateNext sx={{ color: '#6366F1', fontSize: '14px' }} />
                            </IconButton>
                          </Tooltip>

                          <Stack
                            gap="1rem"
                            alignItems="center"
                            flexDirection="row"
                          >
                            <Avatar
                              src={`data:image/png;base64,${selectedPatient?.profileImage}`}
                              sx={{ textTransform: 'uppercase !important' }}
                            >{`${selectedPatient?.lastName?.at(0) || ''}${
                              selectedPatient?.firstName?.at(0) || ''
                            }`}</Avatar>
                            <Stack gap=".1rem">
                              <Typography
                                sx={{
                                  display: 'flex',
                                  gap: 1.5,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                  },
                                }}
                                data-testid={therapyPlanningTestId('selected-patient')}
                                onClick={(event) => {
                                  if (selectedPatient?.eocId && selectedPatient?.uuid) {
                                    dispatch(getPatientIdAction(selectedPatient?.uuid));
                                    dispatch(
                                      switchTherapistDisciplineAction(
                                        getTherapistProfile?.discipline
                                      )
                                    );
                                    navigate(
                                      `${patients}/${patientDetails}${episodeOfCare}${details}`,
                                      {
                                        state: {
                                          episodeOfCareId: selectedPatient?.eocId,
                                          getPatientId: selectedPatient?.uuid,
                                        },
                                      }
                                    );
                                  }
                                }}
                                variant="h5"
                              >
                                {`${selectedPatient?.lastName || ''} ${
                                  selectedPatient?.firstName || ''
                                }`}
                                {selectedPatient?.therapyPlanningStatus === 'REQUIRES PLANNING' &&
                                  isRequiredPlanning && (
                                    <Tooltip
                                      placement="top"
                                      title="Planning required"
                                    >
                                      <PriorityHigh
                                        sx={{
                                          width: '20px',
                                          height: '20px',
                                          borderRadius: '50%',
                                          backgroundColor: '#F79009',
                                          padding: '1px',
                                          color: '#fff',
                                          marginTop: '7px',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                              </Typography>
                              <Stack
                                flexDirection={'row'}
                                alignItems={'center'}
                                justifyContent={'flex-start'}
                                flexWrap={'nowrap'}
                                gap={1}
                                sx={{
                                  // width: '240px',
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                <Tooltip title={headerPayerPlanName ? headerPayerPlanName : null}>
                                  <Typography
                                    sx={{ display: 'flex', gap: '6px', color: neutral[900] }}
                                    variant="captionText"
                                  >
                                    <img
                                      alt=""
                                      src={patientListIcon}
                                    />
                                    {headerPayerPlanName
                                      ? headerPayerPlanName.length > 10
                                        ? `${headerPayerPlanName.slice(0, 10)}...`
                                        : headerPayerPlanName
                                      : '-'}
                                    {windowPayer?.payerStartDate && (
                                      <span
                                        data-testid={therapyPlanningTestId('payer-start')}
                                        style={{ fontSize: '10px', fontWeight: 600 }}
                                      >
                                        {' '}
                                        {/* a bare date parses as UTC midnight, the previous evening in the US */}
                                        (started{' '}
                                        {format(
                                          new Date(`${windowPayer.payerStartDate}T00:00:00`),
                                          'MMM. d, yy'
                                        )}
                                        )
                                      </span>
                                    )}
                                  </Typography>
                                </Tooltip>
                                <DotIcon />
                                <Typography
                                  variant="captionText"
                                  sx={{ color: neutral[900] }}
                                >
                                  {' '}
                                  {selectedPatient?.planTracking
                                    ? selectedPatient?.planTracking
                                    : 'Non - PDPM'}{' '}
                                </Typography>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Stack>
                        <Divider
                          sx={{
                            background: '#D4D4D4',
                            width: '2px',
                            height: '40px',
                          }}
                          orientation="vertical"
                        />
                        {therapyCaseMixTemplatesEnabled && (
                          <>
                            <RecommendedRangeHeader data={recommendedRangeData} />
                            <Divider
                              sx={{
                                background: '#D4D4D4',
                                width: '2px',
                                height: '40px',
                              }}
                              orientation="vertical"
                            />
                          </>
                        )}

                        <Stack
                          gap="1rem"
                          alignItems="center"
                          flexDirection="row"
                        >
                          <Tabs
                            indicatorColor="primary"
                            scrollButtons="auto"
                            sx={{
                              // mt: 3,
                              '& .MuiTabs-flexContainer': {
                                gap: '.5rem',
                              },
                              '& .MuiTab-root:not(.Mui-selected):not(.Mui-disabled)': {
                                color: 'text.primary',
                              },
                            }}
                            textColor="primary"
                            onChange={(event, value) => {
                              value === 'details'
                                ? navigate(`${therapyPlanning.index}${therapyPlanning.details}`, {
                                    state: { patientId },
                                  })
                                : value === 'info'
                                ? navigate(
                                    `${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.info}`,
                                    {
                                      state: { patientId },
                                    }
                                  )
                                : value === 'authorization'
                                ? navigate(
                                    `${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.authorization}`,
                                    {
                                      state: { patientId },
                                    }
                                  )
                                : '';
                            }}
                            variant="scrollable"
                            value={
                              location.pathname ===
                              `${therapyPlanning.index}${therapyPlanning.details}`
                                ? 'details'
                                : location.pathname ===
                                  `${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.info}`
                                ? 'info'
                                : location.pathname ===
                                  `${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.authorization}`
                                ? 'authorization'
                                : ''
                            }
                          >
                            {tabs.map((tab, index) => (
                              <Tab
                                key={index}
                                data-testid={therapyPlanningRowTestId('tab', tab?.value)}
                                sx={{ minHeight: '50px' }}
                                disabled={
                                  (tab?.value === 'authorization' &&
                                    !selectedPatient?.isAuthorized) ||
                                  (tab?.value === 'info' && selectedPatient?.isNonTherapy)
                                }
                                label={
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700 }}
                                  >
                                    {index === 1 &&
                                      shouldShowAlertIcon(
                                        dateOfServiceResponse?.guardrailType,
                                        dateOfServiceResponse?.isMdsPresent
                                      ) && (
                                        <Box
                                          sx={{
                                            display: 'inline-block',
                                            position: 'relative',
                                            top: '2px',
                                            mr: 1,
                                          }}
                                        >
                                          <AlertIcons />
                                        </Box>
                                      )}
                                    {tab.label}{' '}
                                  </Typography>
                                }
                                value={tab.value}
                                icon={
                                  tab?.value === 'authorization' &&
                                  selectedPatient?.isAuthorized &&
                                  !authDetails?.uuid && (
                                    <IconButton
                                      sx={{
                                        backgroundColor: '#FEE4E2',
                                        padding: '4px',
                                        '&:hover': {
                                          backgroundColor: '#FEE4E2',
                                        },
                                      }}
                                    >
                                      <QuestionMarkOutlinedIcon
                                        sx={{ color: '#B42318', fontSize: '1rem' }}
                                      />
                                    </IconButton>
                                  )
                                }
                                iconPosition="end"
                              />
                            ))}
                          </Tabs>
                        </Stack>
                        {location.pathname?.split('/').pop() === 'info' && !isTabletScreen && (
                          <TherapyPlaningNavigation />
                        )}
                      </Stack>
                      <Button
                        sx={{ height: 'max-content', padding: '6px 14px', gap: '.5rem' }}
                        variant="outlined"
                        size="small"
                        onClick={() => setOpen(true)}
                        data-testid={therapyPlanningTestId('poc')}
                        startIcon={<PlanOfCareIcon />}
                      >
                        POC
                      </Button>
                    </Stack>
                    {isTabletScreen && (
                      <Stack
                        mt={'.1rem'}
                        alignItems={'center'}
                      >
                        {location.pathname?.split('/').pop() === 'info' && (
                          <TherapyPlaningNavigation />
                        )}
                      </Stack>
                    )}

                    <Outlet />
                  </Stack>
                ) : (
                  <Box sx={{ flex: '8', paddingTop: '1.5rem', minWidth: '675px', height: '80vh' }}>
                    <Typography
                      variant="h5"
                      data-testid={therapyPlanningTestId('no-patient')}
                    >
                      No patient record found
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Container>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
        open={updateTherapyPlanningResponse?.loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Drawer
        anchor="right"
        onClose={onClose}
        open={open}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 500,
          },
        }}
      >
        <Stack>
          <Stack
            justifyContent="space-between"
            sx={{ margin: '1.5rem' }}
            flexDirection="row"
          >
            <Typography variant="h5">{`${selectedPatient?.lastName} ${selectedPatient?.firstName}'s Plan of Care`}</Typography>
            <IconButton onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          {/* //new Code */}
          <Stack
            flex={5}
            gap="1.5rem"
          >
            <Card sx={{ padding: '1.5rem' }}>
              <Typography
                sx={{ fontSize: '18px !important' }}
                variant="customHeading"
              >
                Patient Details
              </Typography>
              {!planOfCareResponse?.loading && (
                <Stack
                  sx={{ marginTop: '1rem' }}
                  gap="1rem"
                >
                  <Stack gap=".5rem">
                    <Typography
                      sx={{ marginTop: '1rem' }}
                      variant="subtitle2"
                    >
                      Eval Disciplines
                    </Typography>
                    <Stack
                      flexDirection="row"
                      gap="1rem"
                    >
                      {planOfCareDetails?.evalDisciplines
                        ?.split(',')
                        ?.map((details: any, index: number) => (
                          <Chip
                            key={index}
                            sx={{
                              backgroundColor: '#F3F4F6',
                              color: '#111927',
                              borderRadius: '12px',
                            }}
                            label={details}
                          />
                        ))}
                      {(planOfCareDetails?.evalDisciplines?.split(',')?.length === 0 ||
                        !planOfCareDetails?.evalDisciplines) && <Typography>-</Typography>}
                    </Stack>
                  </Stack>
                  <Stack gap=".5rem">
                    <Typography variant="subtitle2">ICD Codes</Typography>
                    <Stack
                      gap="1rem"
                      flexDirection="row"
                      flexWrap="wrap"
                    >
                      {mdsAssessmentDetails?.icdCodes?.map((icdCode: any, index: number) => (
                        <Tooltip
                          key={index}
                          placement="bottom-end"
                          title="Represents the issues related to Pain in any of the knees."
                        >
                          <Chip
                            variant="outlined"
                            label={`${icdCode} | ${mdsAssessmentDetails?.icdCodesDescription[
                              icdCode?.split(' ')?.at(0)
                            ]}`}
                            sx={{
                              border: '1px solid #00000042',
                              fontSize: '13px',
                              color: '#111927',
                            }}
                          />
                        </Tooltip>
                      ))}
                      {(mdsAssessmentDetails?.icdCodes?.length === 0 ||
                        mdsAssessmentDetails?.icdCodes === undefined) && <Typography>-</Typography>}
                    </Stack>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2">Module</Typography>
                    <Typography variant="caption2Text">
                      {mdsAssessmentDetails?.module || '-'}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="subtitle2">Payer Type</Typography>
                    <Typography variant="caption2Text">{payerPlanName || '-'}</Typography>
                  </Stack>
                </Stack>
              )}
              {mdsAssessmentResponse?.loading && <LoadingSkeletons height={20} />}
            </Card>
          </Stack>

          {!planOfCareResponse?.loading && (
            <Stack
              gap="1rem"
              sx={{ margin: '1.3rem' }}
            >
              <Stack gap=".75rem">
                <Typography
                  variant="paragraphHeading"
                  color="primary"
                >
                  Certifications
                </Typography>
                <Stack
                  alignItems="center"
                  flexDirection="row"
                  position={'relative'}
                  mb={1}
                >
                  <Typography
                    variant="body2"
                    mr={15}
                    position={'absolute'}
                    sx={{ color: '#6C737F' }}
                  >
                    {'Discipline'}
                  </Typography>
                  <Typography
                    variant="body2"
                    ml={15}
                    position={'absolute'}
                    sx={{ color: '#6C737F' }}
                  >
                    {'Start Date'}
                  </Typography>
                  <Typography
                    variant="body2"
                    ml={35}
                    position={'absolute'}
                    sx={{ color: '#6C737F' }}
                  >
                    {'End Date'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack
                  flexWrap="wrap"
                  gap="1rem"
                  flexDirection="column"
                >
                  {renderCertDate('pt')}
                  {renderCertDate('ot')}
                  {renderCertDate('slp')}
                </Stack>
              </Stack>
              <Stack gap=".75rem">
                {' '}
                <Divider />
                <Typography
                  variant="paragraphHeading"
                  color="primary"
                >
                  DIAGNOSIS
                </Typography>
                {/* <Stack
                 flexWrap="wrap"
                 gap="1rem"
                 flexDirection="row"
               >
                 {planOfCareDetails?.diagnosis?.map((name: any, index: any) => (
                   <>
                     <Typography variant="body1">{name}</Typography>
                     {index != planOfCareDetails?.diagnosis?.length - 1 && (
                       <Typography variant="body1">,</Typography>
                     )}
                   </>
                 ))}
               </Stack> */}
              </Stack>
              <Divider />
              {renderFrequencyDuration('PT', 'frequency')}
              {renderFrequencyDuration('OT', 'frequency')}
              {renderFrequencyDuration('SLP', 'frequency')}
              <Divider />
              {renderFrequencyDuration('PT', 'duration')}
              {renderFrequencyDuration('OT', 'duration')}
              {renderFrequencyDuration('SLP', 'duration')}
              <Divider />
              <Stack
                flexDirection="row"
                gap="1rem"
              >
                <Box sx={iconBoxSx}>
                  <WorkspacePremiumOutlinedIcon />
                </Box>
                <Stack>
                  <Typography variant="body2">Goals</Typography>
                  {planOfCareDetails?.goals?.map((goal: any, index: any) => (
                    <Typography
                      key={index}
                      variant="body2"
                    >
                      {goal}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
              <Divider />
              <Stack
                flexDirection="row"
                gap="1rem"
              >
                <Box sx={iconBoxSx}>
                  <WorkspacePremiumOutlinedIcon />
                </Box>
                <Stack gap=".5rem">
                  <Typography variant="body2">Codes</Typography>
                  <Stack
                    gap="1rem"
                    flexDirection="row"
                    flexWrap={'wrap'}
                  >
                    {planOfCareDetails?.codes?.map((planOfCare: any, index: number) => (
                      <Chip
                        variant="outlined"
                        key={index}
                        label={`${planOfCare} | ${
                          planOfCareDetails?.cptCodeDescription[
                            planOfCare?.includes('(Group)') ? ignoreGroup(planOfCare) : planOfCare
                          ] || ''
                        }`}
                      />
                    ))}
                  </Stack>
                </Stack>
              </Stack>
              <Divider />

              {
                <Stack gap=".5rem">
                  <Stack
                    gap=".5rem"
                    flexDirection="row"
                  >
                    <Typography variant="subtitle2">Concurrent Therapy : </Typography>
                    <Typography variant="body2">
                      {planOfCareDetails?.allowConcurrentTherapy ? 'Allow' : 'Not Allowed'}
                    </Typography>
                  </Stack>
                  <Stack
                    gap="1rem"
                    flexDirection="row"
                  >
                    <Typography variant="subtitle2">Group Therapy : </Typography>
                    <Typography variant="body2">
                      {planOfCareDetails?.allowGroupTherapy ? 'Allow' : 'Not Allowed'}
                    </Typography>
                  </Stack>
                </Stack>
              }
            </Stack>
          )}
          {planOfCareResponse?.loading && <LoadingSkeletons height={30} />}
        </Stack>
      </Drawer>
      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '1183px' } }}
        onClose={() => setManageTempDialog(false)}
        aria-labelledby="customized-dialog-title"
        open={openManageTempDialog}
        fullWidth
      >
        <ManageTherapyPlanningTemplate close={() => setManageTempDialog(false)} />
      </BootstrapDialog>
    </>
  );
};

export default TherapyPlanningDetails_2_0;
